import * as React from 'react'
import * as Redux from 'react-redux'
import { Dispatch } from 'redux'

import { LilSam as BaseLilSam } from './LilSam'
import type { Rule } from './useLilSam'
import * as api from '@owl-nest/api-client/latest'
import * as models from '@owl-nest/models'

import * as projectModel from '../../models/project'
import * as projectAction from '../../../actions/project'
import { RootState } from '../../../reducers'
import * as view from './view'
import { useAuth } from '@owl-nest/redux-wrapper/hooks'
import { useLocation } from 'react-router-dom'
import { isPreview } from '../../hooks/useDisplayMode'
import { useStep } from '../Onboarding/useStep'

export function LilSam(): React.ReactElement<unknown> {
  const dispatch = Redux.useDispatch<Dispatch>()
  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)
  const discussionThread = project.thread || undefined

  const location = useLocation()
  const auth = useAuth()
  const user = auth.type === 'loggedin' ? auth.user : undefined

  // @ts-expect-error ExtraFields mismatch
  const { isDone: isOnboardingDone } = useStep(project)

  const rule: Rule = async (steps, setStep) => {
    // /!\ `content` variable might be set in multiple conditions, in a "fall through" manner.
    // The last matching condition takes precedence.
    let content: () => Promise<React.ReactNode | undefined> = () => Promise.resolve(undefined)

    // no lilsam if onboarding is not finished
    if (!isOnboardingDone) {
      return <></>
    }

    if (!isPreview(location.pathname)) {
      if (
        discussionThread &&
        discussionThread.status === api.DiscussionThreadStatus.UNREAD &&
        user &&
        discussionThread.latest_message.sender.id !== user.id
      ) {
        content = async () => {
          return <view.Message sender={discussionThread.latest_message.sender} project={project} />
        }
      }

      // for old project whithout validation_message
      if (project.validation_message === undefined) {
        if (project.status === api.ProjectStatus.VALIDATED && !steps.validated) {
          content = async () => {
            return (
              <view.Validated
                initialOpen={!steps.validated}
                project={project}
                markStepDone={() => setStep('validated', true)}
              />
            )
          }
        }
      }

      // for new project with validation_message
      if (project.status === api.ProjectStatus.VALIDATED && project.validation_message !== undefined) {
        if (project.validation_message.read_by_owner) {
          content = async () => {
            return (
              <view.Validated initialOpen={true} project={project} markStepDone={() => setStep('validated', true)} />
            )
          }
        } else if (discussionThread) {
          return <view.Message sender={discussionThread.latest_message.sender} project={project} />
        }
      }

      if (project.status === api.ProjectStatus.NEW && !steps.welcome) {
        content = async () => {
          if (!steps.alreadyWelcomeOnce) {
            await new Promise((resolve) => setTimeout(resolve, 5000))
            setStep('alreadyWelcomeOnce', true)
          }
          return (
            <view.Welcome
              initialOpen={!steps.welcome}
              project={project}
              markStepDone={() => setStep('welcome', true)}
            />
          )
        }
      }

      if (
        !steps.accepted &&
        project.answer_code === api.ProjectAnswerCode.ACCEPTED &&
        (project.status === api.ProjectStatus.NEW || project.status === api.ProjectStatus.PENDING)
      ) {
        content = async () => {
          return (
            <view.Accepted
              initialOpen={!steps.accepted}
              project={project}
              markStepDone={() => setStep('accepted', true)}
            />
          )
        }
      }

      if ((steps.welcome || project.status !== api.ProjectStatus.NEW) && !steps.examples) {
        content = async () => {
          if (!steps.alreadyExamplesOnce) {
            await new Promise((resolve) => setTimeout(resolve, 500))
            setStep('alreadyExamplesOnce', true)
          }
          return <view.Examples initialOpen={true} project={project} markStepDone={() => setStep('examples', true)} />
        }
      }

      if (models.project.isAllFieldsetsFilled(project) && project.status === api.ProjectStatus.NEW && !steps.submit) {
        content = async () => {
          return (
            <view.Submit initialOpen={!steps.submit} project={project} markStepDone={() => setStep('submit', true)} />
          )
        }
      }

      if (
        !steps.offboardingSuccess &&
        models.project.isOkpalOffboardingAvailable<projectModel.ExtraFields>(project) &&
        offboardingSuccess(steps, project)
      ) {
        content = async () => {
          return (
            <view.OffboardingSuccess
              initialOpen={true}
              project={project}
              markStepSeen={() => setStep('offboardingSuccess', Math.floor(+new Date() / 1000))}
            />
          )
        }
      }

      if (
        !steps.offboardingFunded &&
        models.project.isOkpalOffboardingAvailable<projectModel.ExtraFields>(project) &&
        offboardingFunded(steps, project)
      ) {
        content = async () => {
          return (
            <view.OffboardingFunded
              initialOpen={true}
              project={project}
              markStepSeen={() => setStep('offboardingFunded', Math.floor(+new Date() / 1000))}
            />
          )
        }
      }
    }

    if (models.project.isComingSoonEnabled(project) && isPreview(location.pathname) && !steps.preview) {
      content = async () => {
        return (
          <view.Preview project={project} markStepSeen={() => setStep('preview', Math.floor(+new Date() / 1000))} />
        )
      }
    }

    if (
      models.project.isComingSoonEnabled(project) &&
      location.pathname.includes('/coming-soon/') &&
      !steps.comingsoon
    ) {
      content = async () => {
        return (
          <view.ComingSoon
            project={project}
            markStepSeen={() => setStep('comingsoon', Math.floor(+new Date() / 1000))}
          />
        )
      }
    }

    return content()
  }

  return (
    <BaseLilSam
      namespace="lilsam"
      project={project as api.Project<'onboarding_steps'>}
      updateSteps={(onboarding_steps) => {
        // @ts-expect-error ExtraFields mismatch
        return dispatch(projectAction.update(project, { onboarding_steps }))
      }}
      rule={{ body: rule, dependencies: [project] }}
    />
  )
}

function offboardingFunded(steps: Record<string, any>, project: projectModel.Project): boolean {
  const date = new Date()
  const currentTimestamp = Math.floor(date.getTime() / 1000)
  const sevenDaysNumeral = 7 * 24 * 60 * 60
  const twentyFourHourNumeral = 24 * 60 * 60
  const littleSamLastDisplayDate = steps.offboardingFunded as number
  const remaingTimeToProjectEndDate =
    project.date_end === undefined ? NaN : Math.floor(+new Date(project.date_end) / 1000) - currentTimestamp
  const postCampaignUrl = project.post_campaign_link?.url

  return (
    models.project.funded<projectModel.ExtraFields>(project) &&
    remaingTimeToProjectEndDate < sevenDaysNumeral &&
    !postCampaignUrl &&
    ((!littleSamLastDisplayDate && remaingTimeToProjectEndDate > twentyFourHourNumeral) ||
      currentTimestamp > littleSamLastDisplayDate + twentyFourHourNumeral)
  )
}

function offboardingSuccess(steps: Record<string, any>, project: projectModel.Project): boolean {
  const date = new Date()
  const currentTimestamp = Math.floor(date.getTime() / 1000)
  const sixMonthAgoTimestamp = Math.floor(date.setMonth(date.getMonth() - 6) / 1000)
  const twentyFourHourNumeral = 24 * 60 * 60
  const littleSamLastDisplayDate = steps.offboardingFunded as number
  const projectEndDateTimestamp = project.date_end === undefined ? NaN : Math.floor(+new Date(project.date_end) / 1000)
  const postCampaignUrl = project.post_campaign_link?.url

  return (
    models.project.success<projectModel.ExtraFields>(project) &&
    sixMonthAgoTimestamp < projectEndDateTimestamp &&
    !postCampaignUrl &&
    (!littleSamLastDisplayDate || currentTimestamp > littleSamLastDisplayDate + twentyFourHourNumeral)
  )
}
